import {
  BALANCE_DELTA,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EMPTY_OBJECT,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  TabSize,
  Toggle,
  columnTypes,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  getAgGridColId,
  getShowJSONContextItem,
  isColumn,
  isGridApiReady,
  removeEmptyFilters,
  selectAll,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useDefaultColumns,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type FilterClause,
  type Leaves,
  type WebsocketRequest,
} from '@talos/kyoko';
import { compact, isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { v1 as uuid } from 'uuid';

import type {
  GetContextMenuItems,
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  SelectionChangedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';
import { getBalanceKey, type Balance, type BalancesFilter } from '../../../types';
import { ShowByButtons } from '../../Portfolio/TreasuryManagement/components/ShowByButtons';
import { CONTROL_TOOLTIPS } from '../tooltips';
import { BalancesBlotterSummaryLine } from './SummaryLine/BalancesBlotterSummaryLine';
import { defaultByCounterpartyColumns } from './defaults';
import type { BalancesBlotterShowBy, BalancesV2TabProps } from './types';
import { useBalancesColumnDefinitions } from './useBalancesV2Columns';
import { BalanceFilterKey, balanceColIDToFilterBuilderKey, useBalancesV2Filter } from './useBalancesV2Filter';
import { useEnrichDeltaBalancesPipe } from './useEnrichedDeltaBalancesObs';

export const balancesBlotterAggSpecs = [
  { valuePath: 'Equivalent.Amount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.AvailableAmount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.OutstandingBuy', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.OutstandingSell', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<Balance>; currencyPath: Leaves<Balance> }[];

interface BalancesV2BlotterParams {
  blotterID: string;
  tab: BalancesV2TabProps;
  onCloneTab: () => void;
  onUpdateTab: (updatedTab: BalancesV2TabProps) => void;
  /** When mounting should the filter accordian be expanded */
  initialIsOpen?: boolean;
}

export const BalancesV2Blotter = ({
  blotterID,
  tab,
  onUpdateTab,
  onCloneTab,
  initialIsOpen,
}: BalancesV2BlotterParams) => {
  const mixpanel = useMixpanel();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultByCounterpartyColumns;
  const defaultFilter = tab.defaultFilter ?? EMPTY_OBJECT;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const showTotals = tab.showTotals ?? true;

  const { homeCurrency } = useDisplaySettings();
  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });
  const updateShowTotals = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowTotals);
    onUpdateTab({ ...tab, showTotals: value });
  });
  const [selectedRows, setSelectedRows] = useState<Balance[]>([]);

  const columnDefinitions = useBalancesColumnDefinitions();
  const defaultColumns = useDefaultColumns(defaultBlotterColumns, columnDefinitions);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filtering = useBalancesV2Filter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    saveFilter: persistedBlotterTable.onFilterChanged,
  });

  const { changeFilter, filterableProperties, initialFilterClauses } = filtering;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter: BalancesFilter = {
          Currencies: filterClausesByPropertyKey.get(BalanceFilterKey.Currencies)?.selections,
          Markets: filterClausesByPropertyKey.get(BalanceFilterKey.Markets)?.selections,
          MarketTypes: filterClausesByPropertyKey.get(BalanceFilterKey.MarketTypes)?.selections,
          Groups: filterClausesByPropertyKey.get(BalanceFilterKey.Groups)?.selections,
          MarketAccounts: filterClausesByPropertyKey.get(BalanceFilterKey.MarketAccounts)?.selections,
        };

        const proposed = removeEmptyFilters({
          ...curr,
          ...newFilter,
        });

        if (isEqual(curr, proposed)) {
          return curr;
        }

        return proposed;
      });
    },
    [changeFilter]
  );

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const { openClause } = filterBuilderAccordion;

  const columnsOnly = useMemo(() => defaultColumns.filter(isColumn), [defaultColumns]);
  const initialRowGroupColumnIds = useMemo(
    () =>
      getRowGroupColumnIdsFromState(persistedBlotterTable.initialState?.gridState) ??
      getRowGroupColumnIdsFromColumns(columnsOnly),
    [columnsOnly, persistedBlotterTable.initialState?.gridState]
  );
  const [groupAccounts, setGroupAccounts] = useState<boolean>(initialRowGroupColumnIds.has('marketAccountGroup'));
  const [showBy, setShowBy] = useState<BalancesBlotterShowBy>(
    initialRowGroupColumnIds.has('Currency') ? 'asset' : 'counterparty'
  );

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    const groupedColIds = getRowGroupColumnIdsFromState(event.state);
    if (groupedColIds != null) {
      setShowBy(groupedColIds.has('Currency') ? 'asset' : 'counterparty');
      setGroupAccounts(groupedColIds.has('marketAccountGroup'));
    }
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey: balanceColIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const { handleClickJson, jsonModal } = useJsonModal();

  const getContextMenuItems: GetContextMenuItems<Balance> = useCallbackRef(
    (params: GetContextMenuItemsParams<Balance>) => {
      const data = params.node?.data;

      const items = compact([
        ...filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey: balanceColIDToFilterBuilderKey,
          mixpanel,
        }),
        data ? getShowJSONContextItem({ params, handleClickJson }) : undefined,
        'separator',
        selectAll(params, mixpanel),
        'separator',
        ...getDefaultContextMenuItems(params),
      ]);

      return compact(items);
    }
  );

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Balance>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    return setSelectedRows(compact(rows));
  }, []);

  const blotterTag = useConstant(`BALANCES_V2_${uuid()}`);

  const pipe = useEnrichDeltaBalancesPipe();

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getBalanceKey,
    aggSpecs: balancesBlotterAggSpecs,
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const request = useMemo(
    () => ({
      name: BALANCE_DELTA,
      tag: blotterTag,
      EquivalentCurrency: homeCurrency,
      ShowZeroBalances: showZeroBalances,
      ...removeEmptyFilters(filtering.filter),
    }),
    [blotterTag, homeCurrency, showZeroBalances, filtering.filter]
  );
  const blotterTable = useWsBlotterTable<WebsocketRequest, Balance>({
    request,
    columns: defaultColumns,
    pipe,
    pinnedRowDataPipe: aggPipe,
    showPinnedRows: showTotals,
    rowID: 'rowID' satisfies keyof Balance,

    getExtraMainMenuItems,
    persistence: persistedBlotterTable,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      groupRemoveSingleChildren: true,
      showOpenedGroup: true,
      autoGroupColumnDef,
      groupDisplayType: 'singleColumn',
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      onSelectionChanged: handleRowSelectionChanged,
    },
    exportOptions: {
      name: 'Balances',
      tabLabel: tab.label,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns, removeRowGroupColumns } = blotterTable;

  const handleShowByChanged = useCallbackRef((showBy: BalancesBlotterShowBy) => {
    const rowGroupColumns: (keyof Balance)[] = showBy === 'asset' ? ['Currency'] : ['marketAccountName'];
    setRowGroupColumns(rowGroupColumns);
  });

  const handleGroupAccountsToggle = useCallbackRef((checked: boolean) => {
    mixpanel.track(MixpanelEvent.GroupAccounts);
    if (checked) {
      setRowGroupColumns(['marketAccountGroup', 'marketAccountName'] satisfies (keyof Balance)[]);
    } else {
      removeRowGroupColumns(['marketAccountGroup'] satisfies (keyof Balance)[]);
    }
  });

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={<ShowByButtons showBy={showBy} onClick={handleShowByChanged} size={TabSize.Default} />}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
              data-testid="collapse-all-button"
            />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Toggle
              size={FormControlSizes.Small}
              checked={showTotals}
              onChange={updateShowTotals}
              label="Show Totals"
            />
            <Toggle
              tooltip={CONTROL_TOOLTIPS.showZeroBalances}
              tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
              size={FormControlSizes.Small}
              checked={showZeroBalances}
              onChange={updateShowZeroBalances}
              label="Show Zero Balances"
            />
            <Toggle
              disabled={showBy === 'asset'}
              size={FormControlSizes.Small}
              checked={groupAccounts}
              onChange={handleGroupAccountsToggle}
              label="Group Accounts"
            />
            <Divider orientation="horizontal" />
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      <BalancesBlotterSummaryLine rows={selectedRows} />
      {jsonModal}
    </>
  );
};

function getRowGroupColumnIdsFromColumns(columns: Column[]): Set<string> {
  return new Set(columns.filter(column => column.rowGroup).map(getAgGridColId));
}

function getRowGroupColumnIdsFromState(state: StateUpdatedEvent['state'] | undefined): Set<string> | undefined {
  return state?.rowGroup?.groupColIds ? new Set(state.rowGroup.groupColIds) : undefined;
}
